/**
 * THE NAARI – Main front-end JavaScript
 *
 * @package Naari
 */

(function () {
	'use strict';

	/**
	 * Banner (hero) entrance: slide-up + fade-up with stagger. Uses GSAP; respects prefers-reduced-motion.
	 */
	function initBannerAnimations() {
		if (typeof window.gsap === 'undefined') return;
		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

		var hero = document.querySelector('.hero');
		if (!hero) return;

		var brand = hero.querySelector('.hero__brand');
		var title = hero.querySelector('.hero__title');
		var tagline = hero.querySelector('.hero__tagline');
		var actions = hero.querySelector('.hero__actions');

		var gsap = window.gsap;

		// Logo: slide-down + fade (from above)
		if (brand) {
			gsap.fromTo(
				brand,
				{ y: -28, opacity: 0 },
				{ y: 0, opacity: 0.6, duration: 0.85, ease: 'power2.out', overwrite: 'auto' }
			);
		}

		// Title, tagline, buttons: slide-up + fade with stagger
		var fadeUpTargets = [title, tagline, actions].filter(Boolean);
		if (fadeUpTargets.length) {
			gsap.fromTo(
				fadeUpTargets,
				{ y: 36, opacity: 0 },
				{
					y: 0,
					opacity: 1,
					duration: 0.9,
					stagger: 0.12,
					delay: 0.15,
					ease: 'power2.out',
					overwrite: 'auto',
				}
			);
		}
	}

	/**
	 * The Seasons Of Her Life: preload images, then switch visible layer on card hover/tap (smooth opacity).
	 */
	function initSeasons() {
		var section = document.getElementById('seasons');
		if (!section) return;

		var layers = section.querySelectorAll('.seasons__image-layer');
		var cards = section.querySelectorAll('.seasons__card');
		var stage = section.querySelector('.seasons__stage');
		if (!layers.length || !cards.length || !stage) return;

		var activeIndex = 0;

		function setActiveIndex(index) {
			if (index === activeIndex) return;
			activeIndex = index;
			layers.forEach(function (layer, i) {
				layer.classList.toggle('seasons__image-layer--active', i === index);
			});
			cards.forEach(function (card, i) {
				var isSelected = i === index;
				card.setAttribute('aria-selected', isSelected ? 'true' : 'false');
				card.setAttribute('tabindex', isSelected ? '0' : '-1');
			});
		}

		function preloadAndSetLayers() {
			layers.forEach(function (layer) {
				var src = layer.getAttribute('data-src');
				if (!src) return;
				var img = new Image();
				img.onload = function () {
					layer.style.backgroundImage = 'url(' + src + ')';
				};
				img.src = src;
			});
		}

		preloadAndSetLayers();

		cards.forEach(function (card, index) {
			card.addEventListener('mouseenter', function () {
				setActiveIndex(index);
			});
			card.addEventListener('focus', function () {
				setActiveIndex(index);
			});
			card.addEventListener('click', function () {
				setActiveIndex(index);
			});
		});

		if (stage) {
			stage.addEventListener('mouseleave', function () {
				// setActiveIndex(0);
			});
		}
	}
	/**
	 * The She Promise: SVG lotus hover.
	 * SVG path order (after Strength): tier1 Youthful+Wise, tier2 Motherhood+Renewal, tier3 Maturity+Nurturing.
	 * Left petals pivot on inner edge toward center (100% 50%); right petals (0% 50%) — mirrored pairs share the same “center” axis.
	 * Center (Strength): lift + shadow + scale; fill unchanged (no fill tween).
	 */
	function initShePromise() {
		var gsap = window.gsap;
		var canAnimatePaths =
			typeof gsap !== 'undefined' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

		var section = document.querySelector('.promise-sec');
		if (!section) return;

		var svg = section.querySelector('.svg-icon svg');
		if (!svg) return;

		var paths = Array.prototype.slice.call(svg.querySelectorAll('path'));
		if (!paths.length) return;

		var centerPath = paths[0];
		var leafPaths = paths.slice(1);

		var PROMISE_LEAF_SCALE = 1.01;
		var PROMISE_LEAF_Y = -2;
		var PROMISE_CENTER_SCALE = 1;

		/** Left-column petals: inner edge is screen-right → pivot 100% 50%. Right column: 0% 50%. */
		var PROMISE_LEFT_LABELS = { youthful: true, motherhood: true, maturity: true };
		var PROMISE_RIGHT_LABELS = { wise: true, renewal: true, nurturing: true };

		function normalizeLabel(value) {
			return String(value || '')
				.trim()
				.toLowerCase();
		}

		function setPromisePathPivot(path) {
			if (!path) return;
			var key = normalizeLabel(path && path.getAttribute ? path.getAttribute('aria-label') : '');
			var originX = '60%';
			if (PROMISE_LEFT_LABELS[key]) {
				originX = '100%';
			} else if (PROMISE_RIGHT_LABELS[key]) {
				originX = '0%';
			}
			gsap.set(path, {
				transformBox: 'fill-box',
				transformOrigin: originX + ' 50%',
			});
		}

		if (canAnimatePaths) {
			paths.forEach(function (p) {
				setPromisePathPivot(p);
			});
			gsap.set(leafPaths, {
				fill: 'transparent',
				clearProps: 'filter',
			});
		}

		function findPromiseLabelEl(label) {
			var needle = normalizeLabel(label);
			if (!needle) return null;

			var strengthTitle = section.querySelector('.svg-title h5');
			if (strengthTitle && normalizeLabel(strengthTitle.textContent) === needle) {
				return strengthTitle.closest('.svg-title') || null;
			}

			var candidates = section.querySelectorAll('.promise-content .pos-abs h5');
			for (var i = 0; i < candidates.length; i++) {
				var h5 = candidates[i];
				if (normalizeLabel(h5.textContent) === needle) {
					return h5.closest('.pos-abs') || null;
				}
			}
			return null;
		}

		function setPromiseLabelActive(path, isActive) {
			var label = path && path.getAttribute ? path.getAttribute('aria-label') : '';
			var el = findPromiseLabelEl(label);
			if (!el) return;
			el.classList.toggle('is-active', Boolean(isActive));
		}

		var interactivePaths = [centerPath].concat(leafPaths);

		interactivePaths.forEach(function (path) {
			if (!path) return;
			path.style.cursor = 'pointer';

			path.addEventListener('mouseenter', function () {
				setPromiseLabelActive(path, true);

				if (!canAnimatePaths) return;

				setPromisePathPivot(path);

				if (path === centerPath) {
					gsap.to(path, {
						duration: 0.7,
						scale: PROMISE_CENTER_SCALE,
						y: PROMISE_LEAF_Y,
						filter: 'drop-shadow(0 14px 24px rgba(50, 16, 39, 0.22))',
						ease: 'power3.out',
						overwrite: 'auto',
					});
					return;
				}

				gsap.to(path, {
					duration: 0.7,
					scale: PROMISE_LEAF_SCALE,
					y: PROMISE_LEAF_Y,
					fill: '#3B112F',
					filter: 'drop-shadow(0 16px 28px rgba(50, 16, 39, 0.28))',
					ease: 'power3.out',
					overwrite: 'auto',
				});
			});

			path.addEventListener('mouseleave', function () {
				setPromiseLabelActive(path, false);

				if (!canAnimatePaths) return;

				if (path === centerPath) {
					gsap.to(path, {
						duration: 0.6,
						scale: 1,
						y: 0,
						filter: 'none',
						ease: 'power2.out',
						overwrite: 'auto',
					});
					return;
				}

				gsap.to(path, {
					duration: 0.6,
					scale: 1,
					y: 0,
					fill: 'transparent',
					filter: 'none',
					ease: 'power2.out',
					overwrite: 'auto',
				});
			});

			path.addEventListener('focus', function () {
				setPromiseLabelActive(path, true);
			});
			path.addEventListener('blur', function () {
				setPromiseLabelActive(path, false);
			});
		});
	}

	/**
	 * Her Ritual: add .is-in-view when section enters viewport for subtle fade-in animation.
	 * Respects prefers-reduced-motion.
	 */
	function initHerRitualScrollAnimation() {
		var section = document.querySelector('.home-ritual');
		var block = section && section.querySelector('.her-ritual');
		if (!block || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

		var observer = new IntersectionObserver(
			function (entries) {
				entries.forEach(function (entry) {
					if (entry.isIntersecting) {
						entry.target.classList.add('is-in-view');
					}
				});
			},
			{ rootMargin: '0px 0px -10% 0px', threshold: 0 }
		);
		observer.observe(block);
	}

	/**
	 * Press & Media: Splide slider – 5 items on desktop, no arrows/dots, Auto Scroll extension.
	 */
	function initPressMediaSplide() {
		var el = document.getElementById('press-media-slider');
		if (!el || typeof window.Splide === 'undefined') return;

		var extensions = window.splide && window.splide.Extensions ? { AutoScroll: window.splide.Extensions.AutoScroll } : {};
		var splide = new window.Splide(el, {
			type: 'loop',
			perPage: 5,
			perMove: 1,
			gap: '1.5rem',
			arrows: false,
			pagination: false,
			drag: true,
			speed: 600,
			autoScroll: {
				speed: 1,
				pauseOnHover: true,
				pauseOnFocus: true,
			},
			breakpoints: {
				1024: { perPage: 4 },
				768: { perPage: 3 },
				480: { perPage: 2 },
			},
		});
		if (Object.keys(extensions).length) {
			splide.mount(extensions);
		} else {
			splide.mount();
		}
	}

	/**
	 * Initialise Splide testimonial carousel on Her Journeys section (front page only).
	 */
	function initTestimonialsSplide() {
		var el = document.getElementById('her-journeys-slider');
		if (!el || typeof window.Splide === 'undefined') {
			return;
		}
		var splide = new window.Splide(el, {
			type: 'slide',
			perPage: 2,
			perMove: 1,
			gap: '6.25rem', // ~100px desktop
			arrows: true,
			pagination: false,
			drag: true,
			speed: 600,
			breakpoints: {
				768: {
					perPage: 2,
					gap: '4rem', // ~60–80px tablet
					arrows: false,
					pagination: true,
				},
				480: {
					perPage: 1,
					gap: '1.5rem',
				},
			},
			ariaLabel: 'Her Journeys testimonials',
			i18n: {
				prev: 'Previous testimonial',
				next: 'Next testimonial',
			},
		});
		splide.mount();
	}

	/**
	 * Signature services (home): vertical stack on small screens (no horizontal carousel).
	 * Touch devices do not get hover — mark the card whose vertical center is closest to the
	 * viewport center while scrolling so it gets the same styles as :hover (.is-scroll-active).
	 */
	function initHomeServicesScrollActive() {
		var section = document.querySelector('.home-services');
		if (!section) {
			return;
		}
		var items = section.querySelectorAll('.service-item');
		if (!items.length) {
			return;
		}

		var mq = window.matchMedia('(max-width: 767px)');
		var rafId = 0;

		function clearActive() {
			for (var i = 0; i < items.length; i++) {
				items[i].classList.remove('is-scroll-active');
			}
		}

		function updateActive() {
			if (!mq.matches) {
				clearActive();
				return;
			}
			var vh = window.innerHeight;
			var centerY = vh / 2;
			var best = null;
			var bestDist = Infinity;
			for (var j = 0; j < items.length; j++) {
				var item = items[j];
				var r = item.getBoundingClientRect();
				var itemCy = r.top + r.height / 2;
				var dist = Math.abs(itemCy - centerY);
				if (dist < bestDist) {
					bestDist = dist;
					best = item;
				}
			}
			clearActive();
			if (best) {
				best.classList.add('is-scroll-active');
			}
		}

		function scheduleUpdate() {
			if (rafId) {
				return;
			}
			rafId = window.requestAnimationFrame(function () {
				rafId = 0;
				updateActive();
			});
		}

		function onMqChange() {
			if (!mq.matches) {
				clearActive();
			} else {
				updateActive();
			}
		}

		window.addEventListener('scroll', scheduleUpdate, { passive: true });
		window.addEventListener('resize', scheduleUpdate);
		if (typeof mq.addEventListener === 'function') {
			mq.addEventListener('change', onMqChange);
		} else if (typeof mq.addListener === 'function') {
			mq.addListener(onMqChange);
		}

		updateActive();
	}

	function init() {
		initBannerAnimations();
		initSeasons();
		initShePromise();
		initHerRitualScrollAnimation();
		initPressMediaSplide();
		initTestimonialsSplide();
		initHomeServicesScrollActive();
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}
})();
